import {
  type BirdFrame,
  drawBirdShape,
  fillBirdWings,
} from './agents-bird.draw';
import { BIRD_SHAPES, type BirdSpecies } from './agents-bird.species';

export type ToneField = {
  size: number;
  darkness: Float32Array;
  brightness: Float32Array;
  coverage: Float32Array;
};

const LIGHT = (() => {
  const length = Math.hypot(0.55, -0.65, 0.5);
  return { x: 0.55 / length, y: -0.65 / length, z: 0.5 / length };
})();

const BODY_SHADE_LIMIT = 0.8;

const boxBlur = (source: Float32Array, size: number, radius: number) => {
  const r = Math.max(1, Math.round(radius));
  const span = 2 * r + 1;
  const result = source.slice();
  const pass = new Float32Array(source.length);
  const clamp = (value: number) => Math.min(size - 1, Math.max(0, value));
  for (let repeat = 0; repeat < 3; repeat++) {
    for (let y = 0; y < size; y++) {
      const row = y * size;
      let sum = 0;
      for (let x = -r; x <= r; x++) sum += result[row + clamp(x)];
      for (let x = 0; x < size; x++) {
        pass[row + x] = sum / span;
        sum += result[row + clamp(x + r + 1)] - result[row + clamp(x - r)];
      }
    }
    for (let x = 0; x < size; x++) {
      let sum = 0;
      for (let y = -r; y <= r; y++) sum += pass[clamp(y) * size + x];
      for (let y = 0; y < size; y++) {
        result[y * size + x] = sum / span;
        sum +=
          pass[clamp(y + r + 1) * size + x] - pass[clamp(y - r) * size + x];
      }
    }
  }
  return result;
};

const rasterize = (
  size: number,
  frame: BirdFrame,
  draw: (context: CanvasRenderingContext2D, scale: number) => void
) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return new Float32Array(size * size);
  const scale = size / frame.side;
  context.setTransform(scale, 0, 0, scale, -frame.x * scale, -frame.y * scale);
  draw(context, scale);
  const pixels = context.getImageData(0, 0, size, size).data;
  const values = new Float32Array(size * size);
  for (let i = 0; i < values.length; i++) values[i] = pixels[i * 4 + 3] / 255;
  return values;
};

export const toneField = (
  species: BirdSpecies,
  frame: BirdFrame,
  size: number,
  wingBeat: number
): ToneField => {
  const shape = BIRD_SHAPES[species];
  const silhouette = rasterize(size, frame, (context, scale) =>
    drawBirdShape(context, shape, scale, wingBeat)
  );
  const wingMask = rasterize(size, frame, (context) =>
    fillBirdWings(context, shape, wingBeat)
  );
  const eyeMask = rasterize(size, frame, (context) => {
    context.beginPath();
    for (const eye of shape.eyes) {
      context.moveTo(eye.x + eye.r, eye.y);
      context.arc(eye.x, eye.y, eye.r, 0, Math.PI * 2);
    }
    context.fill();
  });

  const coverage = new Float32Array(size * size);
  const raisedWing = new Float32Array(size * size);
  const wingHeight = boxBlur(wingMask, size, size * 0.03);
  for (let i = 0; i < coverage.length; i++) {
    coverage[i] = Math.max(silhouette[i], eyeMask[i]);
    raisedWing[i] = wingMask[i] * silhouette[i] * (0.55 + 0.45 * wingHeight[i]);
  }
  const bodyHeight = boxBlur(silhouette, size, size * 0.055);
  const wingLayer = boxBlur(raisedWing, size, 1);
  const castShadow = boxBlur(wingMask, size, size * 0.012);
  const slope = size * 0.28;

  const darkness = new Float32Array(size * size);
  const brightness = new Float32Array(size * size);
  const heightAt = (i: number) => Math.max(bodyHeight[i], wingLayer[i]);
  for (let y = 1; y < size - 1; y++) {
    for (let x = 1; x < size - 1; x++) {
      const i = y * size + x;
      if (coverage[i] <= 0) continue;
      const nx = -(heightAt(i + 1) - heightAt(i - 1)) * 0.5 * slope;
      const ny = -(heightAt(i + size) - heightAt(i - size)) * 0.5 * slope;
      const length = Math.hypot(nx, ny, 1);
      const diffuse = Math.max(
        0,
        (nx * LIGHT.x + ny * LIGHT.y + LIGHT.z) / length
      );
      const shade = Math.min(BODY_SHADE_LIMIT, Math.pow(1 - diffuse, 1.7));
      const shadow = (1 - wingMask[i]) * castShadow[i] * 0.85;
      darkness[i] = Math.max(shade, eyeMask[i], shadow);
      brightness[i] =
        (0.08 + 0.72 * Math.pow(diffuse, 0.9)) *
        (1 - eyeMask[i]) *
        (1 - shadow);
    }
  }

  return { size, darkness, brightness, coverage };
};
